import type { PortInterface } from './types';
import { PortMode } from './types';
import { v4 as uuidv4 } from 'uuid';
import { UnitTypes } from 'unitData/types';
import type {
  UndoFromPortInterface,
  UndoToPortInterface,
} from 'gojs/goDiagram';
import { getPortLimits } from './unitRegistry';

export const getDefaultPorts = (
  id: string,
  layout: { category: string; inputPorts: number; outputPorts: number },
  inputPortKeys?: UndoToPortInterface[],
  outputPortKeys?: UndoFromPortInterface[],
) => {
  const { maxInputPorts, maxOutputPorts } = getPortLimits(
    layout.category as UnitTypes,
  );
  if (
    layout.category === UnitTypes.GRIZZLY_SCREEN ||
    layout.category === UnitTypes.CONVENTIONAL_SCREEN ||
    layout.category === UnitTypes.LINEAR_MOTION_SCREEN ||
    layout.category === UnitTypes.MULTISLOPE_SCREEN ||
    layout.category === UnitTypes.FREEFALL_SCREEN_SF ||
    layout.category === UnitTypes.FREEFALL_SCREEN_SS ||
    layout.category === UnitTypes.GENERIC_SCREEN ||
    layout.category === UnitTypes.BANANA_SCREEN ||
    layout.category === UnitTypes.SCHENCK_SCREEN ||
    layout.category === UnitTypes.KWATANI_SCREEN ||
    layout.category === UnitTypes.GRIZZLY_FEEDER_SW ||
    layout.category === UnitTypes.GRIZZLY_FEEDER_SV ||
    layout.category === UnitTypes.GRIZZLY_FEEDER_SV_E ||
    layout.category === UnitTypes.FEEDER_AND_SCREEN ||
    layout.category === UnitTypes.MOBILE_TRACKED_SCREEN ||
    layout.category === UnitTypes.MOBILE_WHEELED_SCREEN ||
    layout.category === UnitTypes.MOBILE_TRACKED_JAW ||
    layout.category === UnitTypes.MOBILE_WHEELED_JAW ||
    layout.category === UnitTypes.MOBILE_JAW_AND_CONE ||
    layout.category === UnitTypes.MOBILE_VSI ||
    layout.category === UnitTypes.MOBILE_TRACKED_HSI ||
    layout.category === UnitTypes.MOBILE_WHEELED_HSI ||
    layout.category === UnitTypes.MOBILE_TRACKED_CONE ||
    layout.category === UnitTypes.MOBILE_WHEELED_CONE ||
    layout.category === UnitTypes.STORAGE_FEEDER ||
    layout.category === UnitTypes.STORAGE_FEEDER_2_OUTPUTS
  ) {
    return getScreenUnitPorts(
      id,
      maxInputPorts,
      maxOutputPorts,
      inputPortKeys,
      outputPortKeys,
    );
  } else if (
    layout.category === UnitTypes.ADD ||
    layout.category === UnitTypes.SPLIT
  ) {
    return getAddSplitUnitPorts(
      id,
      layout.category,
      maxInputPorts,
      maxOutputPorts,
      inputPortKeys,
      outputPortKeys,
    );
  } else {
    return getUnitPorts(
      id,
      maxInputPorts,
      maxOutputPorts,
      inputPortKeys,
      outputPortKeys,
    );
  }
};

/**
 * Function for adding the default ports for a screen and Feeder Screen
 * @param id id of the associated unit
 * @param inputPorts Number of input ports by default for the unit
 * @param outputPorts Number of output ports by default for the unit
 */
const getScreenUnitPorts = (
  id: string,
  inputPorts: number,
  outputPorts: number,
  inputPortKeys?: UndoToPortInterface[],
  outputPortKeys?: UndoFromPortInterface[],
) => {
  const newPorts: PortInterface[] = [];
  if (typeof inputPortKeys !== 'undefined' && inputPortKeys.length !== 0) {
    let nbrUnconnectedInPorts = inputPorts;
    let tempInputPortIndex = 1;
    inputPortKeys.forEach((inputPort) => {
      newPorts.push({
        key: inputPort.key,
        unitKey: id,
        portMode: PortMode.INPUT,
        portIndex: 0,
      });
      --nbrUnconnectedInPorts;
      tempInputPortIndex++;
    });
    for (let k = 1; k < nbrUnconnectedInPorts + 1; k++) {
      newPorts.push({
        key: uuidv4(),
        unitKey: id,
        portMode: PortMode.INPUT,
        portIndex: tempInputPortIndex,
      });
    }
  } else {
    for (let i = 0; i < inputPorts; i++) {
      newPorts.push({
        key: uuidv4(),
        unitKey: id,
        portMode: PortMode.INPUT,
        portIndex: 0,
      });
    }
  }

  if (typeof outputPortKeys !== 'undefined' && outputPortKeys.length !== 0) {
    let nbrUnconnectedOutPorts = outputPorts;
    let tempPortIndex = 1;
    outputPortKeys.forEach((outputPort) => {
      newPorts.push({
        key: outputPort.key,
        unitKey: id,
        portMode: PortMode.OUTPUT,
        portIndex: tempPortIndex,
      });
      --nbrUnconnectedOutPorts;
      tempPortIndex++;
    });
    for (let p = 1; p < nbrUnconnectedOutPorts + 1; p++) {
      // Här vill vi egentligen använda enums för Screen Ports, ska kolla på det igen
      newPorts.push({
        key: uuidv4(),
        unitKey: id,
        portMode: PortMode.OUTPUT,
        portIndex: tempPortIndex,
      });
    }
  } else {
    for (let j = 1; j < outputPorts + 1; j++) {
      // Här vill vi egentligen använda enums för Screen Ports, ska kolla på det igen
      newPorts.push({
        key: uuidv4(),
        unitKey: id,
        portMode: PortMode.OUTPUT,
        portIndex: j,
      });
    }
  }
  return newPorts;
};

/**
 * A function for adding the default ports for either a ADD or a SPLIT
 * @param id id of the associated unit
 * @param type Either ADD or SPLIT
 * @param inputPorts Number of input ports by default for the unit
 * @param outputPorts Number of output ports by default for the unit
 */
export const getAddSplitUnitPorts = (
  id: string,
  type: string,
  inputPorts: number,
  outputPorts: number,
  inputPortKeys?: UndoToPortInterface[],
  outputPortKeys?: UndoFromPortInterface[],
) => {
  const newPorts: PortInterface[] = [];

  switch (type) {
    case 'ADD':
      if (
        typeof outputPortKeys !== 'undefined' &&
        outputPortKeys.length !== 0
      ) {
        newPorts.push({
          key: outputPortKeys[0].key,
          unitKey: id,
          portMode: PortMode.OUTPUT,
          portIndex: 0,
        });
      } else {
        newPorts.push({
          key: uuidv4(),
          unitKey: id,
          portMode: PortMode.OUTPUT,
          portIndex: 0,
        });
      }
      if (typeof inputPortKeys !== 'undefined' && inputPortKeys.length !== 0) {
        let nbrUnconnectedinPorts = inputPorts;
        let tempPortIndex = 1;
        inputPortKeys.forEach((inputPort) => {
          newPorts.push({
            key: inputPort.key,
            unitKey: id,
            portMode: PortMode.INPUT,
            portIndex: tempPortIndex,
          });
          --nbrUnconnectedinPorts;
          tempPortIndex++;
        });
        for (let p = 1; p < nbrUnconnectedinPorts + 1; p++) {
          newPorts.push({
            key: uuidv4(),
            unitKey: id,
            portMode: PortMode.INPUT,
            portIndex: tempPortIndex,
          });
          tempPortIndex++;
        }
      } else {
        for (let i = 1; i < inputPorts + 1; i++) {
          newPorts.push({
            key: uuidv4(),
            unitKey: id,
            portMode: PortMode.INPUT,
            portIndex: i,
          });
        }
      }
      return newPorts;
    case 'SPLIT':
      if (typeof inputPortKeys !== 'undefined' && inputPortKeys.length !== 0) {
        newPorts.push({
          key: inputPortKeys[0].key,
          unitKey: id,
          portMode: PortMode.INPUT,
          portIndex: 0,
        });
      } else {
        newPorts.push({
          key: uuidv4(),
          unitKey: id,
          portMode: PortMode.INPUT,
          portIndex: 0,
        });
      }
      if (
        typeof outputPortKeys !== 'undefined' &&
        outputPortKeys.length !== 0
      ) {
        let nbrUnconnectedOutPorts = outputPorts;
        let tempPortIndex = 1;
        outputPortKeys.forEach((outputPort) => {
          newPorts.push({
            key: outputPort.key,
            unitKey: id,
            portMode: PortMode.OUTPUT,
            portIndex: tempPortIndex,
          });
          --nbrUnconnectedOutPorts;
          tempPortIndex++;
        });
        for (let l = 1; l < nbrUnconnectedOutPorts + 1; l++) {
          // Här vill vi egentligen använda enums för Screen Ports, ska kolla på det igen
          newPorts.push({
            key: uuidv4(),
            unitKey: id,
            portMode: PortMode.OUTPUT,
            portIndex: tempPortIndex,
          });
        }
      } else {
        for (let j = 1; j < outputPorts + 1; j++) {
          // Här vill vi egentligen använda enums för Screen Ports, ska kolla på det igen
          newPorts.push({
            key: uuidv4(),
            unitKey: id,
            portMode: PortMode.OUTPUT,
            portIndex: j,
          });
        }
      }
      return newPorts;
    default:
      return newPorts;
  }
};

/**
 * Function for adding the default ports for a specific unit
 * @param id id of the associated unit
 * @param inputPorts Number of input ports by default for the unit
 * @param outputPorts Number of output ports by default for the unit
 */
const getUnitPorts = (
  id: string,
  maxInputPorts: number,
  maxOutputPorts: number,
  inputPortKeys?: UndoToPortInterface[],
  outputPortKeys?: UndoFromPortInterface[],
) => {
  const newPorts: PortInterface[] = [];

  if (typeof inputPortKeys !== 'undefined' && inputPortKeys.length !== 0) {
    inputPortKeys.forEach((inputPort) => {
      newPorts.push({
        key: inputPort.key,
        unitKey: id,
        portMode: PortMode.INPUT,
        portIndex: 0,
      });
    });
  } else {
    for (let i = 0; i < maxInputPorts; i++) {
      newPorts.push({
        key: uuidv4(),
        unitKey: id,
        portMode: PortMode.INPUT,
        portIndex: 0,
      });
    }
  }
  if (typeof outputPortKeys !== 'undefined' && outputPortKeys.length !== 0) {
    outputPortKeys.forEach((outputPort) => {
      newPorts.push({
        key: outputPort.key,
        unitKey: id,
        portMode: PortMode.OUTPUT,
        portIndex: 1,
      });
    });
  } else {
    for (let k = 0; k < maxOutputPorts; k++) {
      newPorts.push({
        key: uuidv4(),
        unitKey: id,
        portMode: PortMode.OUTPUT,
        portIndex: 1,
      });
    }
  }
  return newPorts;
};
